import { ImagesType } from "@features/images/type";
import {
  getImageSizeFromSteps,
  getImageUrlFromSteps,
} from "@features/images/utils";
import { useEffectPlease } from "@features/utils";
import _ from "lodash";
import { memo, useEffect, useRef, useState } from "react";
import { Image as KImage, Layer, Stage } from "react-konva";
import { createKonvaFilters } from "./components/actions/effects/cursors";
import { filters } from "./components/actions/effects/filters";

type AdjustmentSettings = {
  brightness?: number;
  contrast?: number;
  saturation?: number;
  hue?: number;
  gamma?: number;
};

const LIVE_PIXEL_RATIO = 0.75; // cheap while sliding
const FULL_PIXEL_RATIO = 1; // recache after idle
const LIVE_WINDOW_MS = 120; // last change considered active
const DEBOUNCE_MS = 220; // idle delay before high quality

export const Preview = memo(
  ({
    image,
    filter: filterName,
    cursorSettings,
  }: {
    image: ImagesType;
    filter: string | null;
    cursorSettings: Partial<AdjustmentSettings>;
  }) => {
    const imageRef = useRef<any>(null);
    const layerRef = useRef<any>(null);
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(
      null
    );

    // timing refs
    const lastChangeAtRef = useRef(0);
    const debounceTimerRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);
    const cachedQualityRef = useRef<"live" | "full" | null>(null);

    // Load the image once
    // We won't use the edited image, always the version before edits
    const lastStepUrl = getImageUrlFromSteps(
      image?.steps.filter((a) => a.mode !== "edits")
    );
    const lastStepSize = getImageSizeFromSteps(
      image?.steps.filter((a) => a.mode !== "edits")
    );
    useEffectPlease(() => {
      if (!lastStepUrl) return;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setImageElement(img);
      img.src = lastStepUrl;
    }, [lastStepUrl]);

    // Helper to cache at a given pixel ratio only when needed
    const ensureCacheQuality = (want: "live" | "full") => {
      const node = imageRef.current;
      if (!node) return;
      if (cachedQualityRef.current === want) return;

      const px = want === "live" ? LIVE_PIXEL_RATIO : FULL_PIXEL_RATIO;
      try {
        node.cache({ pixelRatio: px });
        cachedQualityRef.current = want;
      } catch {}
    };

    // Apply a fast preview on each settings change
    useEffect(() => {
      const node = imageRef.current;
      if (!node || !imageElement) return;

      console.log("draw once", JSON.stringify(cursorSettings), imageElement);

      // We consider this a user change tick
      lastChangeAtRef.current = Date.now();

      // Make sure we are cached at live quality while the user is moving
      ensureCacheQuality("live");

      // Throttle draws with rAF
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          node.getLayer()?.batchDraw();
        });
      }

      // Debounce a full quality recache after idle
      if (debounceTimerRef.current)
        window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = window.setTimeout(() => {
        const now = Date.now();
        // If the last change is still fresh, skip, a new effect will schedule again
        if (now - lastChangeAtRef.current < LIVE_WINDOW_MS) return;

        // Upgrade cache to full quality and redraw once
        ensureCacheQuality("full");
        node.getLayer()?.batchDraw();
      }, DEBOUNCE_MS) as any;

      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }, [cursorSettings, imageElement]);

    // When the image or the set of filters changes, set filters and do a single full cache
    useEffectPlease(() => {
      const node = imageRef.current;
      if (!node || !imageElement) return;
      try {
        // Build filters once per props change, keep your existing logic
        const filter = filterName ? (filters as any)[filterName] : null;
        const cursorFilters = createKonvaFilters(imageRef, cursorSettings);
        const combinedFilters: any = [];
        if (filter) combinedFilters.push(filter as any);
        combinedFilters.push(...cursorFilters);

        console.log("apply filters", filterName, cursorFilters);
        node.filters(combinedFilters);
        // start with full quality once, then the live pass will take over during drag
        node.cache({ pixelRatio: FULL_PIXEL_RATIO });
        cachedQualityRef.current = "full";
        node.getLayer()?.batchDraw();
      } catch (e) {
        console.warn("Error applying filters", e);
      }
    }, [imageElement, filterName, cursorSettings]);

    if (!imageElement) return null;

    return (
      <div
        style={{ aspectRatio: `${imageElement.width / imageElement.height}` }}
      >
        <Stage
          width={lastStepSize.width || 400}
          height={lastStepSize.height || 400}
          perfectDrawEnabled={false}
        >
          <Layer ref={layerRef} hitGraphEnabled={false} listening={false}>
            <KImage
              ref={imageRef}
              image={imageElement}
              width={lastStepSize.width || 400}
              height={lastStepSize.height || 400}
              listening={false}
            />
          </Layer>
        </Stage>
      </div>
    );
  },
  (oldProps, props) => _.isEqual(oldProps, props)
);
